import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Spinner } from '@klaro7/ui';
import { UserRole } from '@klaro7/shared-types';

import { useAuth } from '@/features/auth/hooks/useAuth';
import { useCallHistory } from '../hooks/useCallHistory';
import { callStatusInfo } from '../lib/callStatusInfo';
import { formatMoney, sessionAmount } from '../lib/callAmount';
import { DeviceIcon } from '../lib/deviceInfo';
import { SessionTariffBadges, TariffSummaryBar } from '../lib/tariffProvenance';

const callTypeLabels: Record<string, string> = {
  audio: 'Audio',
  video: 'Video',
  chat: 'Chat',
};

function formatCallDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m === 0 ? `${s}s` : `${m}m ${s}s`;
}

function formatEuro(amount: number): string {
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount);
}

interface PopulatedProfile {
  profile?: { firstName?: string; lastName?: string };
}

/**
 * Importo originale + valuta, con il convertito in EUR. Oggi tutto è in EUR e
 * la seconda riga non compare; con un'altra valuta mostra "≈ € X" al cambio
 * salvato sulla sessione, o "—" se il cambio non è stato registrato.
 */
function AmountCell({ session }: { session: Parameters<typeof sessionAmount>[0] }) {
  if (!(session.totalCost > 0)) {
    return <td className="whitespace-nowrap px-2 py-2 text-right font-medium text-neutral-700">—</td>;
  }
  const a = sessionAmount(session);
  const converted = a.amountEur != null ? formatEuro(a.amountEur) : '—';
  const tooltip = [
    `Importo originale: ${formatMoney(a.amount, a.currency)} (${a.currency})`,
    `Convertito (EUR): ${converted}`,
    a.isEur
      ? 'Nessuna conversione: pagamento in EUR'
      : a.exchangeRate
        ? `Cambio al momento della transazione: ${a.exchangeRate}`
        : 'Cambio non registrato',
  ].join('\n');
  return (
    <td className="whitespace-nowrap px-2 py-2 text-right" title={tooltip}>
      <span className="font-medium text-neutral-700">{formatMoney(a.amount, a.currency)}</span>
      {!a.isEur && (
        <span className="block text-[11px] text-neutral-500">{a.amountEur != null ? `≈ ${converted}` : 'EUR: —'}</span>
      )}
    </td>
  );
}

/**
 * Admin call-history card. Filtered by caller (calls a user made) or by
 * professional (calls/chat sessions a consultant received); with neither id
 * it shows every session on the platform with both counterpart columns.
 */
export function AdminCallHistoryCard({ callerId, professionalId }: { callerId?: string; professionalId?: string }) {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const isSuperAdmin = hasRole(UserRole.SUPER_ADMIN);
  const canOpenDetail = isSuperAdmin;
  const [page, setPage] = useState(1);
  const { data, isLoading, isError } = useCallHistory({ callerId, professionalId, page, limit: 10 });
  const items = data?.data ?? [];
  const pg = data?.pagination;
  const proView = Boolean(professionalId);
  const globalView = !callerId && !professionalId;

  return (
    <div className="rounded-xl border border-neutral-200 bg-white">
      <div className="border-b border-neutral-100 px-5 py-3.5">
        <h3 className="text-sm font-semibold text-neutral-700">
          {globalView ? 'Tutte le attività' : 'Storico chiamate'}
        </h3>
        <p className="text-xs text-neutral-500">
          {globalView
            ? 'Chiamate e sessioni chat della piattaforma'
            : proView
              ? 'Chiamate e sessioni chat ricevute dagli utenti'
              : "Chiamate effettuate dall'utente verso i professionisti"}
        </p>
      </div>
      <div className="p-5">
        {data?.tariffSummary && <TariffSummaryBar summary={data.tariffSummary} />}
        {isLoading ? (
          <div className="flex justify-center py-6"><Spinner /></div>
        ) : isError ? (
          <p className="text-sm text-red-600">Errore nel caricamento delle chiamate.</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-neutral-400">Nessuna chiamata.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-neutral-100 text-sm">
              <thead className="text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                <tr>
                  <th className="px-2 py-2">Data</th>
                  {globalView ? (
                    <>
                      <th className="px-2 py-2">Utente</th>
                      <th className="px-2 py-2">Professionista</th>
                    </>
                  ) : (
                    <th className="px-2 py-2">{proView ? 'Utente' : 'Professionista'}</th>
                  )}
                  <th className="px-2 py-2">Tipo</th>
                  <th className="px-2 py-2">Dispositivo</th>
                  <th className="px-2 py-2">Esito</th>
                  <th className="px-2 py-2">Tariffa</th>
                  <th className="px-2 py-2 text-right">Durata</th>
                  {isSuperAdmin ? (
                    <th
                      className="px-2 py-2 text-right"
                      title="Importo nella valuta originale; sotto, se diversa da EUR, il convertito in EUR al cambio del momento della transazione"
                    >
                      Importo
                    </th>
                  ) : (
                    <th className="px-2 py-2 text-right">Costo</th>
                  )}
                  {isSuperAdmin && (
                    <th className="px-2 py-2 text-right" title="Compenso reale del consulente per la chiamata (esclusi i minuti pagati con bonus)">
                      Guadagno consulente
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {items.map((session) => {
                  const statusInfo = callStatusInfo(session);
                  const profileName = (p?: PopulatedProfile['profile']) =>
                    p ? `${p.firstName || ''} ${p.lastName || ''}`.trim() || '—' : '—';
                  const callerName = profileName((session.callerId as unknown as PopulatedProfile)?.profile);
                  const proName = profileName(
                    (session.professionalId as unknown as { userId?: PopulatedProfile })?.userId?.profile,
                  );
                  return (
                    <tr
                      key={session.id}
                      className={canOpenDetail ? 'cursor-pointer hover:bg-neutral-50' : undefined}
                      onClick={canOpenDetail ? () => navigate(`/dashboard/admin/calls/${session.id}`) : undefined}
                    >
                      <td className="whitespace-nowrap px-2 py-2 text-neutral-500">
                        {new Date(session.initiatedAt).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </td>
                      {globalView ? (
                        <>
                          <td className="whitespace-nowrap px-2 py-2 font-medium text-neutral-900">{callerName}</td>
                          <td className="whitespace-nowrap px-2 py-2 font-medium text-neutral-900">{proName}</td>
                        </>
                      ) : (
                        <td className="whitespace-nowrap px-2 py-2 font-medium text-neutral-900">
                          {proView ? callerName : proName}
                        </td>
                      )}
                      <td className="px-2 py-2 text-neutral-500">{callTypeLabels[session.callType] ?? session.callType}</td>
                      <td className="whitespace-nowrap px-2 py-2">
                        <DeviceIcon userAgent={session.callerUserAgent} deviceModel={session.callerDeviceModel} />
                      </td>
                      <td className="px-2 py-2">
                        <span className={`inline-block whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${statusInfo.color}`}>
                          {statusInfo.label}
                        </span>
                      </td>
                      <td className="px-2 py-2">
                        <SessionTariffBadges session={session} />
                      </td>
                      <td className="whitespace-nowrap px-2 py-2 text-right text-neutral-500">
                        {session.durationSeconds > 0 ? formatCallDuration(session.durationSeconds) : '—'}
                      </td>
                      {isSuperAdmin ? (
                        <AmountCell session={session} />
                      ) : (
                        <td className="whitespace-nowrap px-2 py-2 text-right font-medium text-neutral-700">
                          {session.totalCost > 0 ? formatEuro(session.totalCost) : '—'}
                        </td>
                      )}
                      {isSuperAdmin && (
                        <td className="whitespace-nowrap px-2 py-2 text-right">
                          <span className="font-medium text-neutral-700">
                            {session.consultantEarning != null ? formatEuro(session.consultantEarning) : '—'}
                          </span>
                          {session.conversionEarning != null && session.conversionEarning > 0 && (
                            <span
                              className="block text-[11px] text-brand-teal"
                              title="Bonus conversione: il cliente ha ricaricato dopo questa chiamata (pagato a parte)"
                            >
                              +{formatEuro(session.conversionEarning)} conversione
                            </span>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {pg && pg.totalPages > 1 && (
          <div className="mt-3 flex items-center justify-between">
            <span className="text-xs text-neutral-500">{pg.total} chiamate · pag. {pg.page}/{pg.totalPages}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={!pg.hasPrevPage} onClick={() => setPage((p) => Math.max(1, p - 1))}>Precedente</Button>
              <Button variant="outline" size="sm" disabled={!pg.hasNextPage} onClick={() => setPage((p) => p + 1)}>Successiva</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
